import { withErrorBoundary } from "@sentry/react";
import { compact } from "lodash";
import { useEffect, useState } from "react";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  PricingTier,
} from "types/graphql-schema";

import AskAI from "@apps/ask-ai/ask-ai";
import { ChatSessionContext } from "@apps/ask-ai/ask-ai-context";
import ProfileCoachingInsights from "@apps/profile/overview-tab/components/profile-coaching-insights";
import ProfilePrivateNotes from "@apps/profile/profile-private-notes";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import AppError from "@components/error/error";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import InfoTooltip from "@components/tooltip/info-tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNullWithStringId } from "@helpers/helpers";
import { isNextMeetingInGroup } from "@helpers/meetings";

import { meetingSidebarAriaLabel } from "./meeting-view-sidebar-constants";
import { getWorkHistoryDateRange } from "./work-history-date-range";

const CollapsibleSection = ({
  title,
  expandedUiPreferenceKey,
  rightSide,
  children,
}: {
  title: string;
  expandedUiPreferenceKey: keyof UiPreferenceCache;
  rightSide?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const expanded = !!uiPreferenceCache[expandedUiPreferenceKey];
  const handleToggle = () => {
    saveUiPreference({ [expandedUiPreferenceKey]: !expanded });
  };
  return (
    <div className="border-b last:border-b-0">
      <div className="py-4 flex items-center justify-between gap-2">
        <button
          className="tf-header-sm-dark flex items-center gap-2 py-1 px-2 rounded-md -ml-2 hover:bg-gray-100"
          onClick={handleToggle}
        >
          {title}
          <ToggleChevronIcon expanded={expanded} />
        </button>
        {rightSide}
      </div>
      {expanded && <div className="pb-4">{children}</div>}
    </div>
  );
};

const PrivateNotesVisibilityIndicator = ({
  explanation,
}: {
  explanation: string;
}) => (
  <div className="flex items-center gap-2 text-gray-500 font-medium text-xs">
    <TopicflowIcon type="eyeOff" className="h-4 w-4" />
    Only visible to you
    <InfoTooltip text={explanation} />
  </div>
);

const MeetingViewSidebar = ({
  meeting,
  // Which UI-preference toggles the sidebar. Defaults to the meeting page's
  // key; the profile 1-on-1 tab reuses this sidebar under its own key.
  sidebarExpandedUiPreferenceKey = "meetingSidebarExpanded",
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  sidebarExpandedUiPreferenceKey?: keyof UiPreferenceCache;
}) => {
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const meetingGroup = meeting?.meetingGroup;
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    null,
  );
  const [startedChatSession, setStartedChatSession] = useState(false);

  useEffect(() => {
    // when changing meeting, we reset Ask AI
    setAskAIChatSessionId(null);
    setStartedChatSession(false);
  }, [meeting.id]);

  const expanded = !!uiPreferenceCache[sidebarExpandedUiPreferenceKey];
  const privateNotesCalloutAcknowledged =
    uiPreferenceCache.privateNotesCalloutAcknowledged;
  if (!expanded || !meetingGroup) return null;

  const participants = assertEdgesNonNullWithStringId(meeting.participants);
  const participantUsers = compact(participants.map(({ user }) => user));
  const subject = participantUsers.find(
    ({ id }) => id === meeting?.subject?.id,
  );
  const oneOnOneSubject = meetingGroup?.isFormalOneonone ? subject : null;
  const currentUserIsManagerOfSubject =
    !!oneOnOneSubject &&
    (currentUser.directReports.edges || []).some(
      (dr) => dr?.node?.id === oneOnOneSubject.id,
    );
  const subjectIsCurrentUser = currentUser.id === oneOnOneSubject?.id;
  const isInReportingTree = !!meeting.subject?.isInCurrentUserReportingTree;

  const canViewMyNotes =
    !!meetingGroup?.isFormalOneonone &&
    !!oneOnOneSubject &&
    (subjectIsCurrentUser || currentUserIsManagerOfSubject);

  const privateNotesExplanation = oneOnOneSubject
    ? oneOnOneSubject.id === currentUser.id
      ? "These notes are available in the sidebar within your profile, as well as any 1:1 meetings you have with your manager. Any notes you add here will be available in both places."
      : `These notes are available in the sidebar within${
          oneOnOneSubject.name ? ` ${oneOnOneSubject.name}'s` : " this person's"
        } profile, as well as any 1:1 meetings you have with them. Any notes you add here will be available in both places.`
    : "";

  const showWorkHistorySection =
    !!currentOrganization.featureFlags.workHistory &&
    !!meetingGroup?.isFormalOneonone &&
    !!meeting.subject;

  const showCoachingInsightsSection =
    waffle.flag_is_active("coaching-insights") &&
    !!meetingGroup?.isFormalOneonone &&
    !!oneOnOneSubject &&
    currentUserIsManagerOfSubject &&
    isNextMeetingInGroup({
      previousMeeting: {
        startDatetime: meeting.previousMeeting?.startDatetime,
      },
      startDatetime: meeting.startDatetime,
    });

  const {
    startDate: workHistoryStartDate,
    endDate: workHistoryEndDate,
    startDateIsInFuture: workHistoryStartDateIsInFuture,
  } = getWorkHistoryDateRange({
    previousMeetingStartDate: meeting.previousMeeting?.startDate,
    meetingStartDate: meeting.startDate,
  });

  const askAIEnabled =
    !!currentOrganization.featureFlags.askAi &&
    currentOrganization.pricingTier === PricingTier.Ultimate;

  const isChatActive = !!askAIChatSessionId || startedChatSession;

  const handleCloseChat = () => {
    setAskAIChatSessionId(null);
    setStartedChatSession(false);
  };

  return (
    <aside
      className={classNames(
        "w-96 xl:w-120 2xl:w-144 bg-[#FCFCFD]",
        "fixed right-0 bottom-0 top-14 z-rightSidebar",
        "border-l tf-print-display-none",
        "flex flex-col overflow-hidden",
      )}
      aria-label={meetingSidebarAriaLabel}
    >
      <ChatSessionContext.Provider value={{ meetingId: meeting.id }}>
        {isChatActive && (
          <div className="px-6 py-4 flex items-center justify-between shrink-0">
            <div className="tf-header-sm-dark">New chat</div>
            <Button
              icon={getTopicflowIcon("close")}
              onClick={handleCloseChat}
              theme={buttonTheme.iconGray}
              aria-label="Close chat"
            />
          </div>
        )}
        {!isChatActive && (
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-6">
              {canViewMyNotes && oneOnOneSubject && (
                <CollapsibleSection
                  title="My Notes"
                  expandedUiPreferenceKey="meetingSidebarMyNotesExpanded"
                  rightSide={
                    <PrivateNotesVisibilityIndicator
                      explanation={privateNotesExplanation}
                    />
                  }
                >
                  {privateNotesCalloutAcknowledged ? (
                    <ProfilePrivateNotes
                      profileId={oneOnOneSubject.id}
                      disabled={false}
                    />
                  ) : (
                    <div className="flex gap-3 items-start rounded-md bg-gray-100 p-4">
                      <TopicflowIcon
                        type="info"
                        className="h-4 w-4 mt-0.5 shrink-0 text-gray-500"
                      />
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <p className="text-sm font-semibold text-gray-800">
                            This notes area is only visible to you
                          </p>
                          <p className="text-sm text-gray-600">
                            {privateNotesExplanation}
                          </p>
                        </div>
                        <button
                          className="self-start rounded-md border border-gray-200 bg-white px-2 py-0.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          onClick={() =>
                            saveUiPreference({
                              privateNotesCalloutAcknowledged: true,
                            })
                          }
                        >
                          Acknowledge and Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </CollapsibleSection>
              )}
              {showCoachingInsightsSection && oneOnOneSubject && (
                <CollapsibleSection
                  title="Coaching Insights"
                  expandedUiPreferenceKey="meetingSidebarCoachingInsightsExpanded"
                >
                  <ProfileCoachingInsights
                    profile={oneOnOneSubject}
                    alwaysShowPanel={false}
                    variant="embedded"
                  />
                </CollapsibleSection>
              )}
              {showWorkHistorySection && meeting.subject && (
                <CollapsibleSection
                  title="Work History"
                  expandedUiPreferenceKey="meetingSidebarWorkHistoryExpanded"
                  rightSide={
                    !workHistoryStartDateIsInFuture && (
                      <div className="text-gray-500 font-medium text-xs">
                        {`${workHistoryStartDate.format(
                          "MMM D, YYYY",
                        )} - ${workHistoryEndDate.format("MMM D, YYYY")}`}
                      </div>
                    )
                  }
                >
                  {workHistoryStartDateIsInFuture ? (
                    <div className="text-sm text-gray-500">
                      This meeting is too far into the future to have a work
                      history, please check back later!
                    </div>
                  ) : (
                    <WorkHistorySidebar
                      userId={meeting.subject.id}
                      startDate={workHistoryStartDate.format("YYYY-MM-DD")}
                      endDate={workHistoryEndDate.format("YYYY-MM-DD")}
                      hasViewingPermission={
                        subjectIsCurrentUser || isInReportingTree
                      }
                      sectionClassName="px-0"
                      hideSummaryHeader
                    />
                  )}
                </CollapsibleSection>
              )}
            </div>
          </div>
        )}
        {askAIEnabled && (
          <div
            className={classNames(
              isChatActive ? "flex-1 flex flex-col min-h-0" : "shrink-0",
            )}
          >
            <AskAI
              isInSidebar
              chatSessionId={askAIChatSessionId}
              onChangeChatSessionId={setAskAIChatSessionId}
              isFullScreen={isChatActive}
              className={classNames("px-6 pb-6", !isChatActive && "!pt-0")}
              totalYPaddingAroundContainer={110}
              onStartChatSession={() => {
                setStartedChatSession(true);
              }}
            />
          </div>
        )}
      </ChatSessionContext.Provider>
    </aside>
  );
};

const MeetingViewSidebarWithSentry = withErrorBoundary(MeetingViewSidebar, {
  fallback: (
    <AppError description={"The meeting sidebar could not be displayed."} />
  ),
});

export default MeetingViewSidebarWithSentry;
